import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../Context';
import { AuthContextApi } from '../../Context/api';
import {
    CalendarIcon,
    InformationCircleIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'
import { toast } from 'react-toastify';
import CardMetricas from '../../Components/Cards/Metricas';
import Header from '../../Components/Header';
import FormBusca from '../../Components/Forms/Busca';
import { colorMap } from '../../Common';
import FaturasTable from '../../Components/Table/Faturas';
import ModalVisualizacaoFatura from './ModalVisualizacao';
import ModalFatura from '../../Components/Modal/Faturas';
import { jsonConfig } from '../../Config';

const Faturas = () => {

    const { states, setters } = useContext(AuthContext)
    const {
        checkLogin,
        getFaturas,
        downloadFaturaPDF,
        postFatura,
        patchFatura,
        deleteFatura,
        geracaoNovaFatura,
        getFaturaEdicao
    } = useContext(AuthContextApi)

    const navigate = useNavigate()
    const [query, setQuery] = useState('')
    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)

    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('numero_referencia')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)

    const [modalVisualizacao, setModalVisualizacao] = useState(false)
    const [faturaSelecionada, setFaturaSelecionada] = useState(null)

    const filterOptions = [
        { value: 'numero_referencia', label: 'Número de Referência' },
        { value: 'cliente_nome_empresarial', label: 'Cliente' },
        { value: 'cliente_cnpj', label: 'CNPJ' },
        { value: 'cliente_email', label: 'Email' }
    ]

    const sortOptions = [
        { value: 'numero_referencia', label: 'Número de Referência' },
        { value: 'cliente_nome_empresarial', label: 'Cliente' },
        { value: 'data_fatura', label: 'Data da Fatura' },
        { value: 'total', label: 'Valor Total' },
        { value: 'data_criacao', label: 'Data de Criação' }
    ]

    const primeiroLoad = async () => {
        const params = new URLSearchParams({
            page: 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: ''
        })
        await checkLogin()
        await getFaturas(params.toString())
    }

    const loadData = async () => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getFaturas(params.toString())
    }

    const next = async () => {
        setters.setPage(states.page + 1)
    }

    const previous = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
        }
    }

    const search = async (data) => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getFaturas(params.toString())
    }

    const clear = async () => {
        setters.setPage(0)
        const params = new URLSearchParams({
            page: 0,
            per_page: 10,
            sort: 'data_fatura',
            sort_dir: 'desc',
            filter: 'numero_referencia',
            search: ''
        })
        await getFaturas(params.toString())
    }

    const color = 'purple'
    const colorClasses = colorMap[color] || colorMap.purple

    const userPermission = localStorage.getItem('permisssion')
    const isAdmin = userPermission === jsonConfig.roles.admin

    const viewFatura = (fatura) => {
        setFaturaSelecionada(fatura)
        setModalVisualizacao(true)
    }

    const addFatura = () => {
        setters.setIsEdit(false)
        setters.setFatura(null)
        setters.setModalFatura(true)
    }

    const actionModalFaturaCreate = async (data) => {
        const response = await postFatura(data)
        if (response) {
            setters.setModalFatura(false)
            primeiroLoad()
        }
    }

    const actionModalFaturaEdit = async (data) => {
        const response = await patchFatura(data)
        if (response) {
            setters.setModalFatura(false)
            setters.setIsEdit(false)
            setters.setFatura(null)
            primeiroLoad()
        }
    }

    const actionDeleteFatura = async (id) => {
        const response = await deleteFatura(id)
        if (response) {
            primeiroLoad()
        }
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            const isAtStart = scrollLeft <= 1
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1
            setShowLeftArrow(!isAtStart)
            setShowRightArrow(!isAtEnd)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const containerRect = tableContainerRef.current.getBoundingClientRect()
            const scrollRect = tableScrollRef.current.getBoundingClientRect()
            const headerHeight = scrollRect.top - containerRect.top
            const tableHeight = scrollRect.height
            const topPosition = headerHeight + (tableHeight / 2)
            const containerHeight = containerRect.height
            const percentage = (topPosition / containerHeight) * 100
            setArrowTop(`${percentage}%`)
        }
    }

    const headers = [
        {
            label: 'Ações',
            icon: DocumentTextIcon,
            key: 'actions'
        },
        {
            label: 'Referência',
            icon: InformationCircleIcon,
            key: 'numero_referencia'
        },
        {
            label: 'Cliente',
            icon: InformationCircleIcon,
            key: 'cliente_nome_empresarial'
        },
        {
            label: 'CNPJ',
            icon: InformationCircleIcon,
            key: 'cliente_cnpj'
        },
        {
            label: 'Data da Fatura',
            icon: CalendarIcon,
            key: 'data_fatura'
        },
        {
            label: 'Valor Total',
            icon: InformationCircleIcon,
            key: 'total'
        },
        {
            label: 'Criação',
            icon: CalendarIcon,
            key: 'data_criacao'
        }
    ]

    useEffect(() => {
        const tableElement = tableScrollRef.current
        if (tableElement) {
            const timer = setTimeout(() => {
                checkScrollPosition()
                calculateArrowPosition()
            }, 1)

            tableElement.addEventListener('scroll', checkScrollPosition)
            window.addEventListener('resize', () => {
                checkScrollPosition()
                calculateArrowPosition()
            })

            return () => {
                clearTimeout(timer)
                tableElement.removeEventListener('scroll', checkScrollPosition)
                window.removeEventListener('resize', checkScrollPosition)
            }
        }
    }, [states.dataFaturas, states.loading])

    useEffect(() => {
        primeiroLoad()

        return () => {
            setQuery('')
            setters.setDataFaturas([])
            setters.setFatura({})
            setters.setModalFatura(false)
        }
    }, [])

    useEffect(() => {
        if (states.dataFaturas.length > 0) {
            search()
        }
    }, [states.page])

    return (
        <>
            <Header
                title="Faturas"
                description="Visualize e cadastre faturas do sistema"
                icon={DocumentTextIcon}
                buttonText="Nova Fatura"
                buttonAction={addFatura}
                hasAction={true}
                hasReturn={false}
                color='purple'
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {isAdmin && <CardMetricas dataMetricas={null} color={color} />}
                <FormBusca
                    filterOptions={filterOptions}
                    sortOptions={sortOptions}
                    search={search}
                    handleClear={clear}
                    color={color}
                    items={{
                        searchTerm,
                        setSearchTerm,
                        selectedFilterField,
                        setSelectedFilterField,
                        selectedSortField,
                        setSelectedSortField,
                        sortDirection,
                        setSortDirection,
                        recordsPerPage,
                        setRecordsPerPage,
                    }}
                    haveData={states.dataFaturas.length > 0}
                />

                <FaturasTable
                    data={states.dataFaturas}
                    loading={states.loading}
                    title="Faturas"
                    headers={headers}
                    viewFatura={viewFatura}
                    color={color}
                    previous={previous}
                    next={next}
                    page={states.page}
                    isAdmin={isAdmin}
                    geracaoNovaFatura={geracaoNovaFatura}
                    downloadFaturaPDF={downloadFaturaPDF}
                    deleteFatura={actionDeleteFatura}
                    editFatura={(fatura) => {
                        setters.setFatura(fatura)
                        setters.setIsEdit(true)
                        setters.setModalFatura(true)
                    }}
                />
            </div>

            <ModalVisualizacaoFatura
                isOpen={modalVisualizacao}
                onClose={() => setModalVisualizacao(false)}
                fatura={faturaSelecionada}
                color={color}
                downloadFaturaPDF={downloadFaturaPDF}
                geracaoNovaFatura={geracaoNovaFatura}
            />

            <ModalFatura
                states={states}
                setters={setters}
                actionCreate={actionModalFaturaCreate}
                actionEdit={actionModalFaturaEdit}
                color={color}
            />
        </>
    )
}

export default Faturas;

